import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { getIpActivitySearchStatus, getIpActivitySearchResults, fetchTenants } from '../api/client';
import Badge from './ui/Badge';
import Pagination from './Pagination';

const POLL_INTERVAL_MS = 3000;
const TERMINAL = ['Completed', 'Failed', 'Errored', 'Stopped', 'Suspended'];

function fmtDate(s) {
  if (!s) return '—';
  try { return format(parseISO(s), 'MMM d, yyyy HH:mm:ss'); } catch { return s; }
}

function fmtShortDate(s) {
  if (!s) return '—';
  try { return format(parseISO(s), 'MMM d HH:mm'); } catch { return s; }
}

function compareVals(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function SortIndicator({ active, dir }) {
  if (!active) return <span className="ml-1 text-gray-700">{'↕'}</span>;
  return dir === 'asc'
    ? <ChevronUpIcon className="inline h-3 w-3 ml-1" />
    : <ChevronDownIcon className="inline h-3 w-3 ml-1" />;
}

function FlagPill({ on, label, color = 'red' }) {
  if (!on) return null;
  const cls = {
    red:    'bg-red-900/40 text-red-300 border-red-800',
    amber:  'bg-amber-900/40 text-amber-300 border-amber-800',
    blue:   'bg-blue-900/40 text-blue-300 border-blue-800',
    purple: 'bg-purple-900/40 text-purple-300 border-purple-800',
  }[color];
  return <span className={`inline-block text-xs px-2 py-0.5 rounded border ${cls}`}>{label}</span>;
}

// Attribution tiers, in display priority (most concerning first).
const ATTRIBUTION_TIERS = ['attacker', 'ms_backend', 'user_baseline', 'unknown'];
const ATTRIBUTION_META = {
  attacker:      { label: 'attacker',      shortLabel: 'A',  tone: 'bg-red-900/50 text-red-200 border-red-700',     headerTone: 'bg-red-900/30 border-red-800',     textTone: 'text-red-200',     hint: 'Definitively the attacker — clientIP equals the malicious IP.' },
  ms_backend:    { label: 'MS backend',    shortLabel: 'MS', tone: 'bg-blue-900/40 text-blue-200 border-blue-700',  headerTone: 'bg-blue-900/30 border-blue-800',   textTone: 'text-blue-200',    hint: 'Microsoft backend / shared-infra IP. Could be the attacker OR the user driving through MS infrastructure — indeterminate.' },
  user_baseline: { label: 'user baseline', shortLabel: 'U',  tone: 'bg-green-900/40 text-green-200 border-green-700', headerTone: 'bg-green-900/30 border-green-800', textTone: 'text-green-200',   hint: 'IP appears in this user\'s 7-day normal sign-in baseline (silver_m365_7_day_normal). Likely the legitimate user — possibly working concurrently with the attacker on a stolen session.' },
  unknown:       { label: 'unknown',       shortLabel: '?',  tone: 'bg-gray-800 text-gray-300 border-gray-700',      headerTone: 'bg-gray-800/60 border-gray-700',   textTone: 'text-gray-300',    hint: 'Not the malicious IP, not a known Microsoft backend IP, not in the user\'s baseline. Could be a previously-unseen legitimate IP or an attacker pivot — analyst judgment.' },
};
function AttributionBadge({ value }) {
  const m = ATTRIBUTION_META[value] || ATTRIBUTION_META.unknown;
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.tone}`} title={m.hint}>
      {m.label}
    </span>
  );
}

function AttributionBreakdown({ split, compact }) {
  if (!split) return null;
  const a = split.attacker || 0;
  const m = split.ms_backend || 0;
  const b = split.user_baseline || 0;
  const u = split.unknown || 0;
  if (a + m + b + u === 0) return null;
  return (
    <span className={`inline-flex gap-1 ${compact ? 'text-[10px]' : 'text-xs'}`}>
      {a > 0 && <span className="text-red-300"   title={ATTRIBUTION_META.attacker.hint}>A:{a}</span>}
      {m > 0 && <span className="text-blue-300"  title={ATTRIBUTION_META.ms_backend.hint}>MS:{m}</span>}
      {b > 0 && <span className="text-green-300" title={ATTRIBUTION_META.user_baseline.hint}>U:{b}</span>}
      {u > 0 && <span className="text-gray-400"  title={ATTRIBUTION_META.unknown.hint}>?:{u}</span>}
    </span>
  );
}

// Map raw M365 / SharePoint Operation strings to short analyst-readable verbs.
// Anything not in the map falls through to a lower-cased version of the raw op.
const OPERATION_LABEL = {
  // Exchange mailbox
  MailItemsAccessed:        'read',
  Send:                     'sent',
  SendOnBehalf:             'sent (on-behalf)',
  SendAs:                   'sent (as)',
  MoveToDeletedItems:       'deleted',
  HardDelete:               'purged',
  SoftDelete:               'soft-deleted',
  Update:                   'updated',
  Create:                   'created',
  Move:                     'moved',
  ModernMobileSync:         'mobile-sync',
  ModernAttachmentDownload: 'attachment-download',
  // SharePoint / OneDrive
  FileAccessed:           'accessed',
  FileAccessedExtended:   'accessed',
  FileModified:           'modified',
  FileModifiedExtended:   'modified',
  FileDeleted:            'deleted',
  FileUploaded:           'uploaded',
  FileDownloaded:         'downloaded',
  FilePreviewed:          'previewed',
  FileRenamed:            'renamed',
  FileMoved:              'moved',
  FileSyncDownloadedFull: 'sync-down',
  FileSyncUploadedFull:   'sync-up',
  PageViewed:             'viewed',
  CompanyLinkUsed:        'link-clicked',
  SharingLinkUsed:        'shared-link-used',
  AnonymousLinkUsed:      'anon-link-used',
  SharingInvitationCreated: 'shared',
  // Sign-in
  UserLoggedIn:           'login',
  UserLoginFailed:        'login-failed',
};
function operationLabel(op) {
  if (!op) return '?';
  return OPERATION_LABEL[op] || op.toLowerCase();
}

// Tone for an operation badge based on what kind of action it is.
function operationTone(op) {
  const label = operationLabel(op);
  if (/sent|attachment-download|downloaded|sync-up|shared|link-clicked|shared-link-used|anon-link-used/.test(label)) {
    return 'bg-amber-900/40 text-amber-200 border-amber-800';   // outbound — possible exfil
  }
  if (/deleted|purged|soft-deleted/.test(label)) {
    return 'bg-purple-900/40 text-purple-200 border-purple-800'; // destructive — distinct from red (which is reserved for `attacker` attribution)
  }
  if (/login-failed/.test(label)) {
    return 'bg-orange-900/40 text-orange-200 border-orange-800';
  }
  return 'bg-gray-800 text-gray-300 border-gray-700';           // read/access — neutral
}

function OperationBadges({ ops, max = 4 }) {
  if (!ops) return null;
  const entries = Object.entries(ops).filter(([, c]) => c > 0).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return null;
  const visible = entries.slice(0, max);
  const overflow = entries.length - visible.length;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {visible.map(([op, count]) => (
        <span
          key={op}
          title={op}
          className={`text-[10px] leading-4 px-1.5 rounded border ${operationTone(op)}`}
        >
          {operationLabel(op)}{count > 1 ? `×${count}` : ''}
        </span>
      ))}
      {overflow > 0 && <span className="text-[10px] text-gray-500">+{overflow} more</span>}
    </span>
  );
}

// Sectioned list — splits items into one section per attribution tier.
// Each item with byAttribution[tier] > 0 appears in that tier's section
// with the per-tier count, so a single subject seen from BOTH attacker and
// user_baseline appears in both sections (not deduplicated). Empty tiers
// are not rendered.
function SectionedAttributionList({ items, mono, breakAll }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="text-[11px] text-gray-500">No items.</div>;
  }
  const sections = {};
  for (const tier of ATTRIBUTION_TIERS) {
    const rows = [];
    for (const it of items) {
      const c = (it.byAttribution && it.byAttribution[tier]) || 0;
      if (c > 0) rows.push({ name: it.name, count: c, byOperation: it.byOperation });
    }
    rows.sort((a, b) => b.count - a.count);
    sections[tier] = rows;
  }
  const anyRows = ATTRIBUTION_TIERS.some((t) => sections[t].length > 0);
  if (!anyRows) {
    // Backwards-compat: items lacked byAttribution (older blob). Render flat.
    return <CountList items={items} mono={mono} breakAll={breakAll} />;
  }
  return (
    <div className="space-y-2">
      {ATTRIBUTION_TIERS.map((tier) => {
        const rows = sections[tier];
        if (rows.length === 0) return null;
        const m = ATTRIBUTION_META[tier];
        return (
          <div key={tier} className={`border rounded p-2 ${m.headerTone}`}>
            <div className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${m.textTone}`}>
              {m.label} ({rows.length} {rows.length === 1 ? 'item' : 'items'})
            </div>
            <ul className="space-y-0.5 text-xs">
              {rows.map((it, i) => (
                <li key={i} className="flex justify-between gap-3 items-start">
                  <span className="flex flex-wrap items-baseline gap-2 flex-1 min-w-0">
                    <OperationBadges ops={it.byOperation} />
                    <span className={`text-gray-200 ${breakAll ? 'break-all' : 'truncate'} ${mono ? 'font-mono' : ''}`}>{it.name || '(blank)'}</span>
                  </span>
                  <span className="text-gray-500 flex-shrink-0">×{it.count}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export default function IpActivitySearchResultsPage() {
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialState = location.state || {};

  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [tenantMap, setTenantMap] = useState({});
  const pollRef = useRef(null);
  const startedAt = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  // Tab + table state
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'events'
  const [usersSort, setUsersSort] = useState({ key: 'totalHits', dir: 'desc' });
  const [eventsSort, setEventsSort] = useState({ key: 'creationTimeUtc', dir: 'desc' });
  const [matchFilter, setMatchFilter] = useState('all'); // 'all' | 'direct' | 'replay'
  const [search, setSearch] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(25);
  const [eventsPageSize, setEventsPageSize] = useState(50);
  // Expansion state — keyed by user "tenantId|userId" and event id
  const [expandedUserKey, setExpandedUserKey] = useState(null);
  const [expandedEventId, setExpandedEventId] = useState(null);

  // Tenant friendly-name lookup (best-effort, non-blocking)
  useEffect(() => {
    fetchTenants().then((tenants) => {
      if (!Array.isArray(tenants)) return;
      const map = {};
      tenants.forEach((t) => {
        const id = (t.tenantId || t.TenantID || '').toLowerCase();
        const name = t.friendlyName || t.FriendlyName || t.name || t.Name;
        if (id && name) map[id] = name;
      });
      setTenantMap(map);
    }).catch(() => {});
  }, []);

  // Elapsed seconds counter (UI only)
  useEffect(() => {
    if (status && TERMINAL.includes(status.status)) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000)), 500);
    return () => clearInterval(t);
  }, [status]);

  // Polling
  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const s = await getIpActivitySearchStatus(jobId);
        if (cancelled) return;
        setStatus(s);
        if (TERMINAL.includes(s.status)) {
          if (s.status === 'Completed') {
            try {
              const data = await getIpActivitySearchResults(jobId);
              if (!cancelled) setResults(data);
            } catch (e) {
              if (!cancelled) setError(e.message || 'Failed to load results.');
            }
          }
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to fetch search status.');
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      }
    };

    fetchOnce();
    pollRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId]);

  const tenantName = (id) => {
    if (!id) return '—';
    return tenantMap[id.toLowerCase()] || id;
  };

  // ── Derived: filtered + sorted user rollup ──
  const filteredUsers = useMemo(() => {
    if (!results?.users) return [];
    const q = search.trim().toLowerCase();
    return results.users
      .map((u) => ({ ...u, totalHits: (u.directHits || 0) + (u.sessionHits || 0) }))
      .filter((u) => {
        if (!q) return true;
        return (
          (u.tenantId || '').toLowerCase().includes(q) ||
          (u.userId || '').toLowerCase().includes(q) ||
          (tenantName(u.tenantId) || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const cmp = compareVals(a[usersSort.key], b[usersSort.key]);
        return usersSort.dir === 'asc' ? cmp : -cmp;
      });
  }, [results, search, usersSort, tenantMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived: filtered + sorted events ──
  const filteredEvents = useMemo(() => {
    if (!results?.events) return [];
    const q = search.trim().toLowerCase();
    return results.events
      .filter((e) => {
        if (matchFilter === 'direct' && e.matchKind !== 'direct_ip') return false;
        if (matchFilter === 'replay' && e.matchKind !== 'session_replay') return false;
        if (!q) return true;
        return (
          (e.userId || '').toLowerCase().includes(q) ||
          (e.tenantId || '').toLowerCase().includes(q) ||
          (e.clientIP || '').toLowerCase().includes(q) ||
          (e.operation || '').toLowerCase().includes(q) ||
          (e.logType || '').toLowerCase().includes(q) ||
          (tenantName(e.tenantId) || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const cmp = compareVals(a[eventsSort.key], b[eventsSort.key]);
        return eventsSort.dir === 'asc' ? cmp : -cmp;
      });
  }, [results, search, matchFilter, eventsSort, tenantMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const usersPaged = useMemo(() => {
    const start = (usersPage - 1) * usersPageSize;
    return filteredUsers.slice(start, start + usersPageSize);
  }, [filteredUsers, usersPage, usersPageSize]);

  const eventsPaged = useMemo(() => {
    const start = (eventsPage - 1) * eventsPageSize;
    return filteredEvents.slice(start, start + eventsPageSize);
  }, [filteredEvents, eventsPage, eventsPageSize]);

  const toggleUserSort = (key) => {
    setUsersSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
    setUsersPage(1);
  };
  const toggleEventSort = (key) => {
    setEventsSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
    setEventsPage(1);
  };

  const isRunning = status && !TERMINAL.includes(status.status);
  const ip = results?.ipAddress || status?.ipAddress || initialState.ipAddress || '—';
  const enrichment = results?.enrichment || null;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Back link */}
      <button
        onClick={() => navigate('/settings/known-malicious-ips')}
        className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Known Malicious IPs
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-3">
            Cross-Tenant Activity
            <span className="font-mono text-amber-400 text-xl">{ip}</span>
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {status?.startDate || initialState.startDate || '?'} {'→'} {status?.endDate || initialState.endDate || '?'}
            {status?.requestedBy ? <span className="ml-3 text-gray-500">requested by {status.requestedBy}</span> : null}
          </p>
        </div>

        {/* Status pill */}
        {status && (
          <div className="text-right">
            {isRunning ? (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>{status.status} — {elapsed}s</span>
              </div>
            ) : status.status === 'Completed' ? (
              <Badge tone="success">Completed</Badge>
            ) : (
              <Badge tone="critical">{status.status}</Badge>
            )}
            {status.message && isRunning && (
              <p className="text-xs text-gray-500 mt-1">{status.message}</p>
            )}
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-900/30 border border-red-700/50 rounded p-3 mb-4 flex items-start gap-2 text-sm text-red-300">
          <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Failed banner */}
      {status && (status.status === 'Failed' || status.status === 'Errored' || status.status === 'Stopped') && (
        <div className="bg-red-900/30 border border-red-700/50 rounded p-3 mb-4 text-sm text-red-300">
          <div className="font-medium">Search {status.status.toLowerCase()}.</div>
          {status.error && <div className="mt-1 whitespace-pre-wrap font-mono text-xs">{status.error}</div>}
        </div>
      )}

      {/* Loading-while-running placeholder */}
      {isRunning && !results && (
        <div className="card p-6 mb-4">
          <div className="text-gray-400 text-sm">
            Querying Databricks across all tenants for activity tied to this IP, then expanding via session map for token-replay detection.
            This typically takes 30–60 seconds.
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <>
          {/* Enrichment header card */}
          <div className="card p-4 mb-4 grid grid-cols-2 lg:grid-cols-6 gap-3 text-sm">
            {enrichment ? (
              <>
                <div>
                  <div className="text-xs text-gray-500">Country / City</div>
                  <div className="text-gray-200">{enrichment.country || '—'}{enrichment.city ? ` / ${enrichment.city}` : ''}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Provider</div>
                  <div className="text-gray-200 truncate" title={enrichment.provider}>{enrichment.provider || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Network</div>
                  <div className="text-gray-200">{enrichment.network_type || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Risk Level</div>
                  <div className="text-gray-200">{enrichment.risk_level ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Azure Tier</div>
                  <div className="text-gray-200">{enrichment.azure_service_tier || '—'}</div>
                </div>
                <div className="flex flex-wrap gap-1 items-start">
                  <FlagPill on={enrichment.detections_compromised}  label="Compromised" color="red" />
                  <FlagPill on={enrichment.detections_tor}          label="Tor"          color="purple" />
                  <FlagPill on={enrichment.detections_proxy}        label="Proxy"        color="amber" />
                  <FlagPill on={enrichment.detections_vpn}          label="VPN"          color="amber" />
                  <FlagPill on={enrichment.detections_anonymous}    label="Anonymous"    color="amber" />
                </div>
              </>
            ) : (
              <div className="col-span-6 text-sm text-gray-500">
                No IP enrichment record on file. The IP may not have appeared in M365 audit logs before today's enrichment run.
              </div>
            )}
          </div>

          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <SummaryTile label="Direct hits"      value={results.directHitCount ?? 0} tone="red" />
            <SummaryTile label="Session replay"   value={results.sessionPivotCount ?? 0} tone="amber" />
            <SummaryTile label="Sessions touched" value={results.sessionsTouched ?? 0} tone="purple" />
            <SummaryTile label="Tenants affected" value={(results.tenantsAffected || []).length} tone="blue" />
            <SummaryTile label="Users affected"   value={(results.users || []).length} tone="blue" />
          </div>

          {/* Seed-sessions + compromise-window banner */}
          {(Array.isArray(results.seedSessionIds) && results.seedSessionIds.length > 0) || results.compromiseStart ? (
            <div className="bg-gray-900/60 border border-gray-800 rounded p-3 mb-4 text-xs space-y-2">
              {results.compromiseStart && (
                <div>
                  <span className="text-gray-400">Compromise window: </span>
                  <span className="text-gray-200 font-mono">{results.compromiseStart}</span>
                  <span className="text-gray-200"> → {results.endDate || '?'}</span>
                  <span className="text-gray-500"> · session-replay events earlier than this were dropped (pre-compromise legitimate session use).</span>
                </div>
              )}
              {Array.isArray(results.seedSessionIds) && results.seedSessionIds.length > 0 && (
                <div>
                  <div className="text-gray-400 mb-1">
                    Inclusion rule: <span className="text-gray-200">clientIP = {results.ipAddress}</span> OR
                    <span className="text-gray-200"> aadSessionId ∈ seed set ({results.seedSessionIds.length})</span>
                    {results.compromiseStart && <span className="text-gray-200"> AND time ≥ compromise start</span>}.
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {results.seedSessionIds.map((sid) => (
                      <span key={sid} className="font-mono text-amber-300 bg-amber-900/20 border border-amber-800/40 rounded px-1.5 py-0.5">{sid}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Tabs + filters */}
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${activeTab === 'users' ? 'border-blue-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
              >
                Tenants &amp; Users ({filteredUsers.length})
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${activeTab === 'events' ? 'border-blue-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
              >
                Events ({filteredEvents.length})
              </button>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setUsersPage(1); setEventsPage(1); }}
                placeholder="Filter by tenant / user / IP / op..."
                className="input-field text-sm w-72"
              />
              {activeTab === 'events' && (
                <select
                  value={matchFilter}
                  onChange={(e) => { setMatchFilter(e.target.value); setEventsPage(1); }}
                  className="input-field text-sm"
                >
                  <option value="all">All matches</option>
                  <option value="direct">Direct only</option>
                  <option value="replay">Session replay only</option>
                </select>
              )}
            </div>
          </div>

          {/* Users tab */}
          {activeTab === 'users' && (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="w-8 px-2 py-2"></th>
                      <SortableTh label="Tenant"      onClick={() => toggleUserSort('tenantId')}    active={usersSort.key === 'tenantId'}    dir={usersSort.dir} />
                      <SortableTh label="User (UPN)"  onClick={() => toggleUserSort('userId')}      active={usersSort.key === 'userId'}      dir={usersSort.dir} />
                      <SortableTh label="Direct"      onClick={() => toggleUserSort('directHits')}  active={usersSort.key === 'directHits'}  dir={usersSort.dir} />
                      <SortableTh label="Replay"      onClick={() => toggleUserSort('sessionHits')} active={usersSort.key === 'sessionHits'} dir={usersSort.dir} />
                      <SortableTh label="Total"       onClick={() => toggleUserSort('totalHits')}   active={usersSort.key === 'totalHits'}   dir={usersSort.dir} />
                      <SortableTh label="First seen" onClick={() => toggleUserSort('firstSeen')}    active={usersSort.key === 'firstSeen'}   dir={usersSort.dir} />
                      <SortableTh label="Last seen"  onClick={() => toggleUserSort('lastSeen')}     active={usersSort.key === 'lastSeen'}    dir={usersSort.dir} />
                      <th className="px-3 py-2 text-left">Log Types</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersPaged.map((u, i) => {
                      const userKey = `${u.tenantId}|${u.userId}|${i}`;
                      const expanded = expandedUserKey === userKey;
                      return (
                        <React.Fragment key={userKey}>
                          <tr
                            className={`border-t border-gray-800 hover:bg-gray-800/50 cursor-pointer ${u.bridgeOnly ? 'bg-amber-950/20' : ''}`}
                            onClick={() => setExpandedUserKey(expanded ? null : userKey)}
                          >
                            <td className="px-2 py-2 text-gray-500">
                              {expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                            </td>
                            <td className="px-3 py-2 text-gray-300" title={u.tenantId}>{tenantName(u.tenantId)}</td>
                            <td className="px-3 py-2 text-gray-200 font-mono text-xs">
                              <div className="flex items-center gap-2">
                                <span>{u.userId || '—'}</span>
                                {u.bridgeOnly && (
                                  <span
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300 border border-amber-800"
                                    title="No direct sign-in from the malicious IP — this user's resources were touched only via someone else's stolen session."
                                  >
                                    session-touched
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-red-400">{u.directHits || 0}</td>
                            <td className="px-3 py-2 text-amber-400">{u.sessionHits || 0}</td>
                            <td className="px-3 py-2 text-gray-100 font-medium">{u.totalHits}</td>
                            <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap">{fmtDate(u.firstSeen)}</td>
                            <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap">{fmtDate(u.lastSeen)}</td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-1">
                                {(u.logTypes || []).map((lt) => (
                                  <span key={lt} className="text-xs px-1.5 py-0.5 rounded bg-gray-800 text-gray-300 border border-gray-700">{lt}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-2 py-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearch(u.userId || '');
                                  setActiveTab('events');
                                  setEventsPage(1);
                                }}
                                title="Filter the events tab to this user"
                                className="text-gray-500 hover:text-blue-400 p-1"
                              >
                                <FunnelIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="bg-gray-900/40">
                              <td className="px-2 py-3"></td>
                              <td colSpan={9} className="px-3 py-3">
                                <UserBridgeDetails user={u} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {usersPaged.length === 0 && (
                      <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No users matched.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length > 0 && (
                <Pagination
                  currentPage={usersPage}
                  totalPages={Math.max(1, Math.ceil(filteredUsers.length / usersPageSize))}
                  pageSize={usersPageSize}
                  totalItems={filteredUsers.length}
                  onPageChange={setUsersPage}
                  onPageSizeChange={(s) => { setUsersPageSize(s); setUsersPage(1); }}
                />
              )}
            </div>
          )}

          {/* Events tab */}
          {activeTab === 'events' && (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="w-8 px-2 py-2"></th>
                      <SortableTh label="Time"        onClick={() => toggleEventSort('creationTimeUtc')} active={eventsSort.key === 'creationTimeUtc'} dir={eventsSort.dir} />
                      <SortableTh label="Tenant"      onClick={() => toggleEventSort('tenantId')}        active={eventsSort.key === 'tenantId'}        dir={eventsSort.dir} />
                      <SortableTh label="User"        onClick={() => toggleEventSort('userId')}          active={eventsSort.key === 'userId'}          dir={eventsSort.dir} />
                      <SortableTh label="Log Type"    onClick={() => toggleEventSort('logType')}         active={eventsSort.key === 'logType'}         dir={eventsSort.dir} />
                      <SortableTh label="Client IP"   onClick={() => toggleEventSort('clientIP')}        active={eventsSort.key === 'clientIP'}        dir={eventsSort.dir} />
                      <SortableTh label="Attribution" onClick={() => toggleEventSort('attribution')}     active={eventsSort.key === 'attribution'}     dir={eventsSort.dir} />
                      <SortableTh label="Operation"   onClick={() => toggleEventSort('operation')}       active={eventsSort.key === 'operation'}       dir={eventsSort.dir} />
                      <th className="px-3 py-2 text-left">Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventsPaged.map((e, i) => {
                      const eid = e.id || `idx-${i}`;
                      const expanded = expandedEventId === eid;
                      const hasDetails = e.details && Object.keys(e.details).length > 0;
                      return (
                        <React.Fragment key={eid}>
                          <tr
                            className={`border-t border-gray-800 hover:bg-gray-800/50 ${hasDetails ? 'cursor-pointer' : ''}`}
                            onClick={() => hasDetails && setExpandedEventId(expanded ? null : eid)}
                          >
                            <td className="px-2 py-2 text-gray-500">
                              {hasDetails
                                ? (expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />)
                                : null}
                            </td>
                            <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap">{fmtShortDate(e.creationTimeUtc)}</td>
                            <td className="px-3 py-2 text-gray-300 text-xs" title={e.tenantId}>{tenantName(e.tenantId)}</td>
                            <td className="px-3 py-2 text-gray-200 font-mono text-xs">{e.userId || '—'}</td>
                            <td className="px-3 py-2 text-gray-300 text-xs">{e.logType}</td>
                            <td className="px-3 py-2 font-mono text-xs">
                              <span className={e.matchKind === 'session_replay' && (e.clientIP || '').toLowerCase() !== (results.ipAddress || '').toLowerCase() ? 'text-amber-300' : 'text-gray-200'}>
                                {e.clientIP || '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2"><AttributionBadge value={e.attribution} /></td>
                            <td className="px-3 py-2 text-gray-300 text-xs">{e.operation || '—'}</td>
                            <td className="px-3 py-2">
                              {e.matchKind === 'direct_ip'
                                ? <Badge tone="critical">direct</Badge>
                                : <Badge tone="warning">session-replay</Badge>}
                            </td>
                          </tr>
                          {expanded && hasDetails && (
                            <tr className="bg-gray-900/40">
                              <td className="px-2 py-3"></td>
                              <td colSpan={8} className="px-3 py-3">
                                <EventDetails event={e} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {eventsPaged.length === 0 && (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No events matched.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filteredEvents.length > 0 && (
                <Pagination
                  currentPage={eventsPage}
                  totalPages={Math.max(1, Math.ceil(filteredEvents.length / eventsPageSize))}
                  pageSize={eventsPageSize}
                  totalItems={filteredEvents.length}
                  onPageChange={setEventsPage}
                  onPageSizeChange={(s) => { setEventsPageSize(s); setEventsPage(1); }}
                />
              )}
            </div>
          )}

          {/* Empty state */}
          {results && (results.events || []).length === 0 && (
            <div className="card p-6 text-center text-gray-400">
              No activity found for this IP in the selected window.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SortableTh({ label, onClick, active, dir }) {
  return (
    <th
      className="px-3 py-2 text-left cursor-pointer select-none hover:text-gray-300"
      onClick={onClick}
    >
      {label}
      <SortIndicator active={active} dir={dir} />
    </th>
  );
}

function SummaryTile({ label, value, tone }) {
  const colors = {
    red:    'text-red-400',
    amber:  'text-amber-400',
    purple: 'text-purple-300',
    blue:   'text-blue-400',
  };
  return (
    <div className="card p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold ${colors[tone] || 'text-gray-200'}`}>{value}</div>
    </div>
  );
}

// ── Bridge details for a user row in the rollup ─────────────────────────
//
// For "session-touched" users (directHits=0) the question an investigator
// needs to answer is: what did the attacker do to MY data? This panel
// surfaces operation counts, source IPs (so you can tell whether they were
// Microsoft service IPs vs the attacker pivoting), file paths touched in
// SharePoint/OneDrive, mail folders/subjects accessed in Exchange, and any
// inbox-rule parameter changes.
function UserBridgeDetails({ user }) {
  const ops = user.operations || [];
  const ips = user.sourceIPs || [];
  const objs = user.objectIds || [];
  const folders = user.mailFolders || [];
  const subjects = user.mailSubjects || [];
  const ruleParams = user.inboxRuleParams || [];

  const hasAny =
    ops.length > 0 || ips.length > 0 || objs.length > 0 ||
    folders.length > 0 || subjects.length > 0 || ruleParams.length > 0;

  return (
    <div className="space-y-3 text-xs">
      {user.bridgeOnly && (
        <div className="bg-amber-900/20 border border-amber-800/50 text-amber-200 rounded p-2">
          <div className="font-semibold mb-1">Session-touched (no direct sign-in)</div>
          <div className="text-amber-300/80">
            This user did NOT authenticate from the malicious IP. Their resources were modified
            via someone else's stolen session — see "Investigate next" below for what to verify.
          </div>
        </div>
      )}

      {user.attributionCounts && (
        (user.attributionCounts.attacker || 0) +
        (user.attributionCounts.ms_backend || 0) +
        (user.attributionCounts.user_baseline || 0) +
        (user.attributionCounts.unknown || 0)
      ) > 0 && (
        <DetailBlock title="Attribution split (events for this user)">
          <div className="flex items-center gap-4 text-xs flex-wrap">
            {ATTRIBUTION_TIERS.map((tier) => {
              const m = ATTRIBUTION_META[tier];
              const count = user.attributionCounts[tier] || 0;
              if (count === 0) return null;
              return (
                <span key={tier} className={m.textTone} title={m.hint}>
                  <span className="font-semibold">{m.label}</span>: {count}
                </span>
              );
            })}
          </div>
        </DetailBlock>
      )}

      {!hasAny && (
        <div className="text-gray-500">No additional context extracted from these events.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ops.length > 0 && (
          <DetailBlock title="Operations">
            <CountList items={ops} />
          </DetailBlock>
        )}
        {ips.length > 0 && (
          <DetailBlock title="Source IPs (each tagged with the aadSessionId that ties it to the malicious IP)">
            <SourceIpList items={ips} />
          </DetailBlock>
        )}
      </div>

      {objs.length > 0 && (
        <DetailBlock title="SharePoint / OneDrive objects touched (sectioned by attribution)">
          <SectionedAttributionList items={objs} mono breakAll />
        </DetailBlock>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {folders.length > 0 && (
          <DetailBlock title="Exchange mail folders accessed">
            <CountList items={folders} mono />
          </DetailBlock>
        )}
        {subjects.length > 0 && (
          <DetailBlock title="Mail subjects (sectioned by attribution)">
            <SectionedAttributionList items={subjects} />
          </DetailBlock>
        )}
      </div>

      {ruleParams.length > 0 && (
        <DetailBlock title="Inbox rule changes — parameters seen" tone="red">
          <div className="text-amber-300 text-xs mb-1">
            New-InboxRule / Set-InboxRule parameters. If you see <code>ForwardTo</code>,
            <code> RedirectTo</code>, <code>ForwardAsAttachmentTo</code>, or <code>MoveToFolder</code>,
            the attacker likely set up persistence — re-check the rule body in Exchange admin.
          </div>
          <CountList items={ruleParams} />
        </DetailBlock>
      )}

      {user.bridgeOnly && (
        <DetailBlock title="Investigate next" tone="blue">
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>Identify which user's session originated at the malicious IP (the "actor user") and treat them as compromised.</li>
            <li>For each file path above, confirm permissions/sharing. Were files downloaded, exfiltrated, or shared externally?</li>
            <li>Check if any of these files contain regulated data (PHI, PII, financial, source code).</li>
            <li>Search the actor user's mail Sent Items for messages including those subjects/files in the same window.</li>
            <li>Verify no inbox rules were created on this user's mailbox (separate audit log lookup).</li>
          </ul>
        </DetailBlock>
      )}
    </div>
  );
}

// ── Details panel for a single event row ────────────────────────────────
function EventDetails({ event }) {
  const d = event.details || {};
  const hasAny = d && Object.keys(d).length > 0;
  if (!hasAny) return <div className="text-xs text-gray-500">No extracted details.</div>;

  return (
    <div className="space-y-3 text-xs">
      {/* Header chips: ID / session / user-agent / op */}
      <div className="flex flex-wrap items-center gap-3 text-gray-400">
        <span className="inline-flex items-center gap-1">
          <span className="text-gray-500">op:</span>
          <span className={`text-[10px] px-1.5 rounded border ${operationTone(event.operation)}`}>{operationLabel(event.operation)}</span>
        </span>
        <span><span className="text-gray-500">id:</span> <span className="font-mono">{event.id}</span></span>
        {event.aadSessionId && (
          <span><span className="text-gray-500">aadSessionId:</span> <span className="font-mono">{event.aadSessionId}</span></span>
        )}
        {(d.userAgent || event.userAgent) && (
          <span><span className="text-gray-500">user-agent:</span> <span className="font-mono">{d.userAgent || event.userAgent}</span></span>
        )}
        {d.clientInfo && (
          <span><span className="text-gray-500">client:</span> <span className="font-mono">{d.clientInfo}</span></span>
        )}
        {d.mailboxOwner && (
          <span><span className="text-gray-500">mailbox owner:</span> <span className="font-mono">{d.mailboxOwner}</span></span>
        )}
      </div>

      {/* Log-type-specific renderer for the body */}
      {event.logType === 'exchange' && <ExchangeEventBody d={d} />}
      {event.logType === 'sharepoint' && <SharePointEventBody d={d} />}
      {(event.logType === 'signin' || event.logType === 'graph_signin') && <SignInEventBody d={d} />}
      {!['exchange','sharepoint','signin','graph_signin'].includes(event.logType) && <GenericEventBody d={d} />}
    </div>
  );
}

function ExchangeEventBody({ d }) {
  const folders = Array.isArray(d.folders) ? d.folders : null;
  const affected = Array.isArray(d.affectedItems) ? d.affectedItems : null;
  const params = Array.isArray(d.parameters) ? d.parameters : null;
  return (
    <div className="space-y-2">
      {/* Single mail item (Send/SendOnBehalf/SendAs) */}
      {(d.subject || d.internetMessageId || d.parentFolder) && (
        <div className="rounded border border-gray-800 bg-gray-900/40 p-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Mail item</div>
          <div className="grid grid-cols-[110px_1fr] gap-y-0.5 gap-x-3">
            {d.subject && (<><div className="text-gray-500">subject</div><div className="text-gray-100 break-all">{d.subject}</div></>)}
            {d.internetMessageId && (<><div className="text-gray-500">message-id</div><div className="text-gray-200 font-mono break-all">{d.internetMessageId}</div></>)}
            {d.parentFolder && (<><div className="text-gray-500">folder</div><div className="text-gray-200 font-mono break-all">{d.parentFolder}</div></>)}
          </div>
        </div>
      )}

      {/* MailItemsAccessed / Bind / Sync — one or more folders, each with item list */}
      {folders && folders.length > 0 && (
        <div className="rounded border border-gray-800 bg-gray-900/40 p-2">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
            Mail items accessed ({folders.reduce((n, f) => n + (Array.isArray(f.items) ? f.items.length : 0), 0)})
          </div>
          <div className="space-y-2">
            {folders.map((f, i) => (
              <div key={i}>
                <div className="text-[11px] text-gray-400 mb-0.5 font-mono break-all">{f.path || '(folder)'}</div>
                {Array.isArray(f.items) && f.items.length > 0 ? (
                  <ul className="ml-2 space-y-0.5">
                    {f.items.map((it, j) => (
                      <li key={j} className="text-xs">
                        <span className="text-gray-100 break-all">{it.subject || '(no subject)'}</span>
                        {it.sizeBytes && (
                          <span className="text-[10px] text-gray-500 ml-2">({it.sizeBytes} bytes)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="ml-2 text-[11px] text-gray-500 italic">no items recorded for this folder</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MoveToDeletedItems — affectedItems carries subjects */}
      {affected && affected.length > 0 && (
        <div className="rounded border border-red-800/60 bg-red-950/20 p-2">
          <div className="text-[11px] uppercase tracking-wide text-red-300 mb-1">Affected items (deleted / moved)</div>
          <ul className="space-y-0.5">
            {affected.map((it, j) => (
              <li key={j}>
                <div className="text-gray-100 break-all">{it.subject || '(no subject)'}</div>
                <div className="text-[10px] text-gray-500 font-mono break-all">
                  {it.internetMessageId && <span>{it.internetMessageId}</span>}
                  {it.internetMessageId && it.parentFolder && <span> · </span>}
                  {it.parentFolder && <span>{it.parentFolder}</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Inbox-rule changes — parameters list */}
      {params && params.length > 0 && (
        <div className="rounded border border-amber-800/60 bg-amber-950/20 p-2">
          <div className="text-[11px] uppercase tracking-wide text-amber-300 mb-1">Inbox-rule parameters</div>
          {params.some(p => /^(ForwardTo|RedirectTo|ForwardAsAttachmentTo|MoveToFolder|DeleteMessage)$/i.test(p.name || '')) && (
            <div className="text-[11px] text-amber-200 mb-1">⚠ Persistence indicators present (ForwardTo / RedirectTo / MoveToFolder / DeleteMessage).</div>
          )}
          <ul className="space-y-0.5">
            {params.map((p, j) => (
              <li key={j} className="grid grid-cols-[160px_1fr] gap-x-3">
                <span className="text-gray-300 font-mono">{p.name}</span>
                <span className="text-gray-200 font-mono break-all">{p.value || '(empty)'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SharePointEventBody({ d }) {
  return (
    <div className="rounded border border-gray-800 bg-gray-900/40 p-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">SharePoint / OneDrive object</div>
      <div className="grid grid-cols-[140px_1fr] gap-y-0.5 gap-x-3 text-xs">
        {d.sourceFileName && (<><div className="text-gray-500">file</div><div className="text-gray-100 break-all">{d.sourceFileName}</div></>)}
        {d.sourceRelativeUrl && (<><div className="text-gray-500">relative path</div><div className="text-gray-200 font-mono break-all">{d.sourceRelativeUrl}</div></>)}
        {d.objectId && (<><div className="text-gray-500">objectId</div><div className="text-gray-200 font-mono break-all">{d.objectId}</div></>)}
        {d.siteUrl && (<><div className="text-gray-500">site</div><div className="text-gray-200 font-mono break-all">{d.siteUrl}</div></>)}
        {d.eventSource && (<><div className="text-gray-500">source</div><div className="text-gray-200">{d.eventSource}</div></>)}
        {d.itemType && (<><div className="text-gray-500">item type</div><div className="text-gray-200">{d.itemType}</div></>)}
        {d.app && (<><div className="text-gray-500">app</div><div className="text-gray-200">{d.app}</div></>)}
        {d.targetUser && (<><div className="text-gray-500">target user</div><div className="text-gray-200">{d.targetUser}</div></>)}
      </div>
      {d.modifiedProperties && (
        <div className="mt-2 text-[11px]">
          <div className="text-gray-400 mb-0.5">Modified properties</div>
          <pre className="bg-gray-950/60 p-1.5 rounded text-gray-300 overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(d.modifiedProperties, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function SignInEventBody({ d }) {
  return (
    <div className="rounded border border-gray-800 bg-gray-900/40 p-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Sign-in</div>
      <div className="grid grid-cols-[140px_1fr] gap-y-0.5 gap-x-3 text-xs">
        {d.app && (<><div className="text-gray-500">application</div><div className="text-gray-200">{d.app}</div></>)}
        {d.clientApp && (<><div className="text-gray-500">client app</div><div className="text-gray-200">{d.clientApp}</div></>)}
        {d.resultDetail && (<><div className="text-gray-500">result detail</div><div className="text-gray-200">{d.resultDetail}</div></>)}
        {d.logonError && (<><div className="text-gray-500">logon error</div><div className="text-red-300">{d.logonError}</div></>)}
        {d.caStatus && (<><div className="text-gray-500">CA status</div><div className="text-gray-200">{d.caStatus}</div></>)}
        {(d.country || d.state || d.city) && (<><div className="text-gray-500">location</div><div className="text-gray-200">{[d.city, d.state, d.country].filter(Boolean).join(', ')}</div></>)}
      </div>
    </div>
  );
}

function GenericEventBody({ d }) {
  const entries = Object.entries(d);
  return (
    <table className="w-full text-xs">
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k} className="align-top">
            <td className="text-gray-500 pr-3 py-1 whitespace-nowrap">{k}</td>
            <td className="text-gray-200 font-mono break-all py-1">{renderDetailValue(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderDetailValue(v) {
  if (v == null) return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    return (
      <div className="space-y-1">
        {v.map((item, i) => (
          <div key={i} className="bg-gray-900/60 rounded p-1.5">
            {typeof item === 'object' ? <pre className="whitespace-pre-wrap break-all">{JSON.stringify(item, null, 2)}</pre> : String(item)}
          </div>
        ))}
      </div>
    );
  }
  return <pre className="whitespace-pre-wrap break-all">{JSON.stringify(v, null, 2)}</pre>;
}

function DetailBlock({ title, tone, children }) {
  const toneCls = {
    red:  'border-red-800 bg-red-950/20',
    blue: 'border-blue-800/60 bg-blue-950/20',
  }[tone] || 'border-gray-800 bg-gray-900/40';
  return (
    <div className={`rounded p-2 border ${toneCls}`}>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function CountList({ items, mono, breakAll, showAttribution }) {
  return (
    <ul className="space-y-0.5">
      {items.map((it, i) => (
        <li key={i} className={`flex justify-between gap-3 items-baseline ${mono ? 'font-mono' : ''}`}>
          <span className={`text-gray-200 ${breakAll ? 'break-all' : 'truncate'} flex-1`}>{it.name || '(blank)'}</span>
          {showAttribution && it.byAttribution && (
            <span className="flex-shrink-0">
              <AttributionBreakdown split={it.byAttribution} compact />
            </span>
          )}
          <span className="text-gray-500 flex-shrink-0">×{it.count}</span>
        </li>
      ))}
    </ul>
  );
}

// Source-IP list with per-IP aadSessionId breakdown so the analyst can verify
// at a glance that each IP is tied to a session that touched the malicious IP.
// Each IP also carries its `tier` (from azure_service_tier) and `attribution`
// classification (attacker / ms_backend / unknown).
function SourceIpList({ items }) {
  return (
    <ul className="space-y-1.5">
      {items.map((it, i) => {
        const sessions = Array.isArray(it.sessions) ? it.sessions : [];
        const allNoSession = sessions.length > 0 && sessions.every((s) => s.sessionId === '(none)');
        return (
          <li key={i} className="flex flex-col gap-0.5">
            <div className="flex justify-between gap-3 items-baseline">
              <span className="font-mono text-gray-200">{it.name || '(blank)'}</span>
              <span className="flex items-baseline gap-2 flex-shrink-0">
                {it.attribution && <AttributionBadge value={it.attribution} />}
                {it.tier && <span className="text-[10px] text-gray-500">{it.tier}</span>}
                <span className="text-gray-500">×{it.count}</span>
              </span>
            </div>
            {sessions.length === 0 ? null : allNoSession ? (
              <div className="text-[10px] text-gray-500 italic ml-2">
                no AppAccessContext.AADSessionId on these events (typically sign-in / graph_signin direct-hit rows)
              </div>
            ) : (
              <div className="ml-2 space-y-0.5">
                {sessions.map((s, j) => (
                  <div key={j} className="text-[10px] flex justify-between gap-2">
                    {s.sessionId === '(none)' ? (
                      <span className="text-gray-500 italic">no aadSessionId</span>
                    ) : (
                      <span className="text-amber-300/80 font-mono break-all">↳ {s.sessionId}</span>
                    )}
                    <span className="text-gray-600">×{s.count}</span>
                  </div>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
